import { Link } from "react-router-dom";
import { Mail, MapPin, Clock, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF, BBB_URL, BBB_SEAL, SERVICE_AREA, VISIT_TIMES } from "@/lib/images";

export const Footer = () => (
  <footer className="border-t border-espresso/10 bg-espresso text-cream" data-testid="site-footer">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
      <div className="space-y-4">
        <div className="inline-block rounded-2xl bg-cream p-3">
          <Logo />
        </div>
        <p className="max-w-xs text-base leading-relaxed text-cream/70">
          Non-medical companionship care for seniors across Calgary — bringing joy, connection and reassurance home.
        </p>
        <a
          href={BBB_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-bbb-link"
          className="inline-block rounded-xl bg-cream/95 p-2.5 transition-transform duration-300 hover:-translate-y-0.5"
          aria-label="Off Our Rockers Seniors Care BBB Business Review"
        >
          <img src={BBB_SEAL} alt="BBB Accredited Business — Calgary" className="h-12 w-auto" loading="lazy" />
        </a>
      </div>

      <div>
        <h3 className="mb-4 font-serif text-xl font-semibold">Explore</h3>
        <ul className="space-y-2.5 text-base">
          {[
            ["Home", "/", "footer-home-link"],
            ["About Us", "/about", "footer-about-link"],
            ["Services", "/services", "footer-services-link"],
            ["Match Quiz", "/#quiz", "footer-quiz-link"],
            ["Gallery", "/gallery", "footer-gallery-link"],
            ["FAQ", "/faq", "footer-faq-link"],
            ["Contact", "/contact", "footer-contact-link"],
          ].map(([label, to, testid]) => (
            <li key={testid}>
              <Link to={to} data-testid={testid} className="text-cream/70 transition-colors hover:text-gold">
                {label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="mb-4 font-serif text-xl font-semibold">Get in Touch</h3>
        <ul className="space-y-3.5 text-base text-cream/70">
          <li>
            <a href={PHONE_HREF} data-testid="footer-phone-link" className="flex items-center gap-3 transition-colors hover:text-gold">
              <Phone className="h-5 w-5 shrink-0 text-gold" /> {PHONE}
            </a>
          </li>
          <li>
            <a href={EMAIL_HREF} data-testid="footer-email-link" className="flex items-center gap-3 break-all transition-colors hover:text-gold">
              <Mail className="h-5 w-5 shrink-0 text-gold" /> {EMAIL}
            </a>
          </li>
          <li className="flex items-center gap-3">
            <MapPin className="h-5 w-5 shrink-0 text-gold" /> {SERVICE_AREA}
          </li>
          <li className="flex items-center gap-3">
            <Clock className="h-5 w-5 shrink-0 text-gold" /> {VISIT_TIMES}
          </li>
        </ul>
      </div>

      <div>
        <h3 className="mb-4 font-serif text-xl font-semibold">Start Today</h3>
        <p className="mb-5 text-base text-cream/70">
          A free, friendly consultation is the first step to the perfect companion match.
        </p>
        <Link
          to="/contact"
          data-testid="footer-book-consultation-button"
          className="btn-primary !bg-gold !text-espresso !shadow-gold/25 hover:!bg-brand hover:!text-white"
        >
          Book Free Consultation
        </Link>
      </div>
    </div>

    <div className="border-t border-cream/10">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 text-sm text-cream/50 sm:px-6 md:flex-row lg:px-8">
        <p>© {new Date().getFullYear()} Off Our Rockers Seniors Care. All rights reserved.</p>
        <p>Non-medical companionship care · Not a substitute for medical or nursing services.</p>
      </div>
    </div>
  </footer>
);
